import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Link,
  Stack,
  Text,
  Tooltip,
  useBreakpointValue
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { getCookieConsentValue } from 'react-cookie-consent';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import ans006246 from '../assets/assessoria/ans006246.svg';
import backgroundLogin from '../assets/assessoria/login-bg.png';
import wavesLogin from '../assets/assessoria/wavesLogin.webp';
import { LockIcon } from '../assets/icons/LockIcon';
import { UserIcon } from '../assets/icons/UserIcon';
import logo from '../assets/logo_sulamerica_default.svg';
import { Form } from '../components/forms/Form';
import { FloatLabelInput } from '../components/inputs/FloatLabelInput';
import { Content } from '../components/layouts/Content';
import { Footer } from '../components/layouts/Footer';
import { Page } from '../components/layouts/Page';
import { CustomCookieConsent } from '../components/widgets/CustomCookieConsent';
import { useAuthContext } from '../contexts/AuthContext';
import { useKeyLock } from '../hooks/useKeylock';
import { formHasErrors } from '../utils/checkers/formHasErrors';

type UserProps = {
  username: string;
  password: string;
  token?: string;
};

const ValidationSchema = Yup.object().shape({
  username: Yup.string().required('Favor informar'),
  password: Yup.string().required('Favor informar')
});

export const Login = (): JSX.Element => {
  const { login } = useAuthContext();

  const captchaRef = useRef<ReCAPTCHA>(null);

  const consent = getCookieConsentValue('pdnCookie') === 'true' ? true : false;

  const [formIsDisabled, setFormIsDisabled] = useState(consent);

  const [captchaError, setCaptchaError] = useState(false);

  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [hasError, setHasError] = useState(false);

  const [incorrectUser, setIncorrectUser] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<UserProps>({
    resolver: yupResolver(ValidationSchema)
  });

  const handleConsent = () => {
    const consent = getCookieConsentValue('pdnCookie');

    consent === 'true' ? setFormIsDisabled(false) : setFormIsDisabled(true);
  };

  const checkFormErrors = (values: UserProps, errors: object) => {
    return formHasErrors(values, errors);
  };

  const captchaHasError = () => {
    setCaptchaError(true);
    setIsLoggingIn(false);
  };

  const onSubmit = async (data: UserProps) => {
    setCaptchaError(false);
    setIsLoggingIn(true);
    setHasError(false);

    const captchaToken = captchaRef.current?.getValue();

    if (captchaToken) {
      try {
        await login(data, true);
        setIsLoggingIn(false);
      } catch (error: any) {
        if (
          error?.response?.data?.data?.details?.includes('401') ||
          error?.response?.data?.result?.includes('401')
        ) {
          setIncorrectUser(true);
        } else {
          setHasError(true);
        }
        setIsLoggingIn(false);
      }
    } else captchaHasError();
  };

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true
  });

  useEffect(() => {
    checkFormErrors(watch(), errors);

    const consent = getCookieConsentValue('pdnCookie');

    consent === 'true' ? setFormIsDisabled(false) : setFormIsDisabled(true);
  }, [formIsDisabled]);

  // useEffect(() => {
  //   const role = localStorage.getItem('pdn:token');

  //   role?.includes('admin')
  //     ? navigate('/')
  //     : role?.includes('assessoria')
  //     ? navigate('/assessoria')
  //     : role?.includes('corretor') && navigate('/corretor');
  // }, []);

  return (
    <Page
      bg="#fff"
      bgImage={isWideVersion ? backgroundLogin : ''}
      bgRepeat="no-repeat"
      bgPosition="right"
      bgSize="cover"
    >
      <Content display="flex" flexDir="column">
        <Stack
          w={isWideVersion ? '35%' : 'auto'}
          p="4rem"
          align={isWideVersion ? 'normal' : 'center'}
          pointerEvents={formIsDisabled ? 'none' : 'all'}
          opacity={formIsDisabled ? '0.4' : '1'}
          spacing="1.5rem"
        >
          <Stack spacing=".65rem">
            <Image src={logo} w="17.5rem" />
            <Text fontSize="1.2rem" fontWeight="bold" color="#25366C">
              Área exclusiva para parceiros.
            </Text>
          </Stack>
          {hasError && (
            <Flex
              h="3.75rem"
              bg="#FACED2"
              w="full"
              maxW={'405px'}
              p="1.2rem"
              justify="space-between"
              align="center"
              color="#AA2C23"
            >
              <Text>
                Ocorreu um erro. Por favor, tente novamente em instantes
              </Text>

              <Button
                fontWeight="bold"
                variant="ghost"
                _hover={{ background: 'transparent' }}
                cursor="pointer"
                onClick={() => setHasError(false)}
              >
                X
              </Button>
            </Flex>
          )}

          <Form id="login" handleSubmit={handleSubmit} onSubmit={onSubmit}>
            <Stack spacing="1.5rem" align="center">
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FloatLabelInput
                  h="2.64rem"
                  // w="16rem"
                  borderRadius=".75rem"
                  type="text"
                  name="username"
                  label="Usuário"
                  icon={<UserIcon />}
                  placeholder="Digite o seu usuário"
                  register={register}
                  error={
                    incorrectUser ? 'Usuário e/ou senha inválidos' : undefined
                  }
                />
                <Tooltip
                  label="Utilize o mesmo login do portal do corretor"
                  fontSize="sm"
                >
                  <InfoOutlineIcon
                    boxSize={5}
                    style={{
                      marginLeft: '10px',
                      marginTop: '15px'
                    }}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FloatLabelInput
                  h="2.64rem"
                  // w="16rem"
                  borderRadius=".75rem"
                  type="password"
                  name="password"
                  label="Senha"
                  icon={<LockIcon />}
                  placeholder="Digite sua senha"
                  register={register}
                  error={
                    incorrectUser ? 'Usuário e/ou senha inválidos' : undefined
                  }
                />
                <Tooltip
                  label="Utilize a mesma senha do portal do corretor"
                  fontSize="sm"
                >
                  <InfoOutlineIcon
                    boxSize={5}
                    style={{
                      marginLeft: '10px',
                      marginTop: '15px'
                    }}
                  />
                </Tooltip>
              </div>

              <Stack>
                <ReCAPTCHA
                  onChange={() => setCaptchaError(false)}
                  style={{ color: '#AA2C23' }}
                  sitekey={
                    process.env.REACT_APP_RECAPTCHA_SITE_KEY
                      ? process.env.REACT_APP_RECAPTCHA_SITE_KEY
                      : '6LdDnh0hAAAAANtkmJDdqSuojMDEq46YnhYbWXQ5'
                  }
                  ref={captchaRef}
                />
                {captchaError && (
                  <Text fontSize=".75rem" color="#AA2C23">
                    Verifique se você não é um robô
                  </Text>
                )}
              </Stack>
              {/* 
              <HStack justify="space-between" color="#001E64" w="100%">
                <Link href="/primeiro-acesso">
                  <Text textDecor="underline">Primeiro acesso</Text>
                </Link>
              </HStack> */}

              <HStack w="100%" justify="space-between">
                {/* <Link
                  href={`${process.env.REACT_APP_PORTAL_URL}/esqueci-minha-senha/`}
                >
                  <Text textDecor="underline" color="#001E64">
                    Recuperar senha
                  </Text>
                </Link> */}
                <Button
                  type="submit"
                  color="gray.50"
                  colorScheme="orange"
                  isLoading={isLoggingIn}
                  isDisabled={!!checkFormErrors(watch(), errors)}
                  h="3rem"
                  w="9.5rem"
                  borderRadius="133.5px"
                >
                  ACESSAR
                </Button>
              </HStack>
            </Stack>
          </Form>
        </Stack>
      </Content>

      <Footer>
        <Image
          src={wavesLogin}
          w="100%"
          display={isWideVersion ? 'none' : 'normal'}
        />
        <Content minH="2rem" bg="#fff">
          <HStack justify="center">
            <Image src={ans006246} />
            <Image src={ans006246} />
          </HStack>
          <Flex
            w={{
              base: 'auto',
              lg: 'max-content'
            }}
            align="center"
            justify="center"
          >
            <Text
              fontWeight="medium"
              fontSize=".7rem"
              color="#828282"
              textAlign="center"
              p=".2rem"
              whiteSpace="normal"
            >
              2022 SulAmérica - Todos os direitos reservados | CNPJ:
              01.685.053/0013-90 - Rua dos Pinheiros, 1673 - São Paulo/SP - CEP:
              05.422-012
            </Text>
          </Flex>
        </Content>
      </Footer>

      <CustomCookieConsent onAccept={handleConsent}>
        A SulAmérica somente utiliza cookies essenciais a navegabilidade do
        site, para melhorar a sua experiência com nossos canais e serviços,
        inexistindo quaisquer coletas de dados adicionais. Ao utilizar nossos
        serviços e canais, você concorda com a coleta exposta. Para saber mais
        informações confira nossa{' '}
        <Link
          href="https://portal.sulamericaseguros.com.br/politica-de-privacidade/"
          target="_blank"
        >
          <Text color="orange.500" textDecor="underline" display="inline">
            Política de Privacidade
          </Text>
        </Link>{' '}
        e conheça nosso{' '}
        <Link
          href="https://portal.sulamericaseguros.com.br/privacidade/login"
          target="_blank"
        >
          <Text color="orange.500" textDecor="underline" display="inline">
            Portal de Privacidade
          </Text>
        </Link>
        .
      </CustomCookieConsent>
    </Page>
  );
};
